import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import API from "../../../util/API";
import { toast } from "react-toastify";
import {
  RefreshCw,
  Settings,
  Clock,
  ExternalLink,
  Save,
  Edit3,
  Copy,
  AlertTriangle,
  CheckCircle2,
  PauseCircle,
  PackageSearch,
  StopCircle,
  Loader2,
} from "lucide-react";

const ACCENT = "#D2042D";
const cx = (...arr) => arr.filter(Boolean).join(" ");

function getApiBaseUrl() {
  const raw = String(API?.defaults?.baseURL || "").trim();
  return raw.replace(/\/+$/, "");
}

function getApiOrigin() {
  const base = getApiBaseUrl();
  if (!base) return "";
  try {
    return new URL(base).origin;
  } catch {
    return "";
  }
}

const tone = {
  good: {
    badge: "bg-emerald-50 text-emerald-700 border-emerald-100",
    dot: "bg-emerald-500",
  },
  warn: {
    badge: "bg-amber-50 text-amber-700 border-amber-100",
    dot: "bg-amber-500",
  },
  bad: {
    badge: "bg-rose-50 text-rose-700 border-rose-100",
    dot: "bg-rose-500",
  },
  neutral: {
    badge: "bg-slate-50 text-slate-700 border-slate-100",
    dot: "bg-slate-400",
  },
};

const DEFAULT_STOCK_INTERVAL_OPTIONS = [1, 3, 6, 12, 24];
const DEFAULT_STOCK_CONCURRENT_OPTIONS = [300];
const DEFAULT_STOCK_MAX_PRODUCTS_PER_RUN = 0;
const TRACKING_INTERVAL_OPTIONS = [
  { label: "Off", value: 0 },
  { label: "6 Hours", value: 6 },
  { label: "12 Hours", value: 12 },
  { label: "24 Hours", value: 24 },
];

const Card = ({ children, className }) => (
  <div className={cx("bg-white border border-slate-200 rounded-2xl", className)}>{children}</div>
);

const SectionHead = ({ title, subtitle, right }) => (
  <div className="flex items-start justify-between gap-3">
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="h-4 w-[3px] rounded-full" style={{ background: ACCENT }} />
        <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">{title}</h2>
      </div>
      {subtitle ? <p className="mt-1 text-xs text-slate-500 line-clamp-2">{subtitle}</p> : null}
    </div>
    {right ? <div className="shrink-0">{right}</div> : null}
  </div>
);

function normalizeEpoch(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  // seconds -> ms
  if (n < 1e12) return n * 1000;
  return n;
}

function fmtDateTime(ms) {
  if (!ms) return "—";
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return "—";
  }
}

function fmtRunCap(value) {
  const n = Number(value || 0);
  return n > 0 ? String(n) : "All eligible";
}

function fmtDurationFromSeconds(seconds) {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n < 0) return "—";
  const sec = Math.floor(n);
  const days = Math.floor(sec / 86400);
  const hrs = Math.floor((sec % 86400) / 3600);
  const mins = Math.floor((sec % 3600) / 60);
  return `${days}d ${hrs}h ${mins}m`;
}

function readApiError(error, fallback) {
  return String(error?.response?.data?.message || error?.message || fallback);
}

/**
 * Mask middle ~50% (as you asked) using ******.
 * Keeps start and end visible, hides the middle.
 */
function maskMiddle50(v) {
  if (!v) return "—";
  const s = String(v);
  if (s.length <= 8) return "******"; // very short secrets
  const keep = Math.max(3, Math.floor(s.length * 0.25)); // keep 25% head, 25% tail
  const head = s.slice(0, keep);
  const tail = s.slice(-keep);
  return `${head}******${tail}`;
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(String(text ?? ""));
    return true;
  } catch {
    return false;
  }
}

function SecretField({
  label,
  hint,
  valueRaw,
  onChangeRaw,
  onSave,
  saving,
  allowEdit = true,
}) {
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    // When value changes from load/save, return to masked view
    setEditing(false);
  }, [valueRaw]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
            {label}
          </p>
          {hint ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
        </div>

        {allowEdit ? (
          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            className="inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-xs font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors"
          >
            <Edit3 size={14} />
            {editing ? "Cancel" : "Edit"}
          </button>
        ) : null}
      </div>

      <div className="mt-3">
        {!editing ? (
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono text-xs bg-slate-50 border border-slate-200 px-2 py-2 rounded-xl break-all">
              {maskMiddle50(valueRaw)}
            </span>
            <button
              type="button"
              onClick={async () => {
                const ok = await copyToClipboard(valueRaw);
                ok ? toast.success("Copied") : toast.error("Copy failed");
              }}
              className="inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-xs font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors"
              disabled={!valueRaw}
              title="Copy (value stays masked on screen)"
            >
              <Copy size={14} />
              Copy
            </button>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <input
              type="password"
              value={valueRaw}
              onChange={(e) => onChangeRaw(e.target.value)}
              placeholder={`Enter ${label}…`}
              className="flex-1 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
            />
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className={cx(
                "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white transition-colors",
                saving ? "bg-slate-300 cursor-not-allowed" : "bg-slate-900 hover:bg-[#D2042D]"
              )}
            >
              <Save size={16} className={saving ? "opacity-70" : ""} />
              Save
            </button>
          </div>
        )}
      </div>

      <p className="mt-3 text-xs text-slate-500">
        Stored privately. Full value is never rendered on screen after save.
      </p>
    </div>
  );
}

export default function AliExpressApiSys() {
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [savingSecret, setSavingSecret] = useState(false);
  const [isAuthorizing, setIsAuthorizing] = useState(false);

  const [tokenInfo, setTokenInfo] = useState(null);
  const [error, setError] = useState("");
  const authPopupRef = useRef(null);
  const authPollRef = useRef(null);
  const authMessageReceivedRef = useRef(false);
  const stockRunningPrevRef = useRef(false);

  // These are SETTINGS values (private)
  const [appSecret, setAppSecret] = useState("");
  const [trackingSyncLoading, setTrackingSyncLoading] = useState(false);
  const [trackingSyncSaving, setTrackingSyncSaving] = useState(false);
  const [trackingSyncNowLoading, setTrackingSyncNowLoading] = useState(false);
  const [trackingSyncConfig, setTrackingSyncConfig] = useState({
    enabled: true,
    intervalHours: 6,
  });
  const [trackingSyncStatus, setTrackingSyncStatus] = useState(null);
  const [trackingSyncError, setTrackingSyncError] = useState("");
  const [stockSyncLoading, setStockSyncLoading] = useState(false);
  const [stockSyncSaving, setStockSyncSaving] = useState(false);
  const [stockSyncNowLoading, setStockSyncNowLoading] = useState(false);
  const [stockSyncConfig, setStockSyncConfig] = useState({
    enabled: true,
    intervalHours: 6,
    concurrentPerSecond: 300,
    maxProductsPerRun: DEFAULT_STOCK_MAX_PRODUCTS_PER_RUN,
    pausedByBulkImport: false,
  });
  const [stockSyncStatus, setStockSyncStatus] = useState(null);
  const [stockSyncRunning, setStockSyncRunning] = useState(false);
  const [stockSyncError, setStockSyncError] = useState("");
  const [stockIntervalOptions, setStockIntervalOptions] = useState(DEFAULT_STOCK_INTERVAL_OPTIONS);
  const [stockConcurrentOptions, setStockConcurrentOptions] = useState(DEFAULT_STOCK_CONCURRENT_OPTIONS);
  const [activeImportJobs, setActiveImportJobs] = useState([]);
  const [activeImportJobsLoading, setActiveImportJobsLoading] = useState(false);
  const [cancellingImportJobId, setCancellingImportJobId] = useState("");

  const lastUpdatedLabel = useMemo(() => {
    const ms = tokenInfo?._fetched_at_ms;
    return ms ? new Date(ms).toLocaleTimeString() : null;
  }, [tokenInfo]);

  const clearAuthorizeWatcher = useCallback(() => {
    if (authPollRef.current) {
      clearInterval(authPollRef.current);
      authPollRef.current = null;
    }
  }, []);

  const getToken = useCallback(async () => {
    try {
      setError("");
      setLoading(true);
      const res = await API.get("/aliexpress/admin/gettoken");
      setTokenInfo({ ...(res?.data || {}), _fetched_at_ms: Date.now() });
    } catch (e) {
      setError("Failed to load AliExpress token. Please refresh.");
      setTokenInfo(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshToken = async () => {
    try {
      setError("");
      setRefreshing(true);
      await API.get("/aliexpress/admin/refresh");
      toast.success("Token refreshed successfully");
      await getToken();
    } catch (e) {
      setError("Failed to refresh token. Use Authorize Token if both expired.");
      toast.error("Failed to refresh token");
    } finally {
      setRefreshing(false);
    }
  };

  const openAuthorize = () => {
    const base = getApiBaseUrl();
    const endpoint = base ? `${base}/aliexpress/auth` : "/api/aliexpress/auth";
    const url = `${endpoint}?state=ali-index&popup=1`;

    authMessageReceivedRef.current = false;
    setIsAuthorizing(true);

    const popup = window.open(
      url,
      "aliexpress_oauth_popup",
      "popup=yes,width=640,height=760"
    );

    if (!popup) {
      setIsAuthorizing(false);
      toast.error("Popup blocked. Please allow popups for this site.");
      return;
    }

    authPopupRef.current = popup;
    clearAuthorizeWatcher();

    authPollRef.current = setInterval(async () => {
      if (!authPopupRef.current) return;
      if (authPopupRef.current.closed) {
        clearAuthorizeWatcher();
        setIsAuthorizing(false);
        if (!authMessageReceivedRef.current) {
          await getToken();
          toast.info("Authorization window closed. Token status reloaded.");
        }
      }
    }, 500);
  };

  const getAppSecret = useCallback(async () => {
    try {
      const res = await API.get("/settings/key/ali_express_app_secret");
      setAppSecret(res?.data?.value ?? "");
    } catch {}
  }, []);

  const saveAppSecret = async () => {
    try {
      setSavingSecret(true);
      await API.put("/settings", { key: "ali_express_app_secret", value: appSecret });
      toast.success("App Secret saved successfully");
      // After save: keep in state (for copy) but UI remains masked by default.
    } catch {
      toast.error("Failed to save App Secret");
    } finally {
      setSavingSecret(false);
    }
  };

  const loadTrackingSyncConfig = useCallback(async () => {
    try {
      setTrackingSyncError("");
      setTrackingSyncLoading(true);
      const res = await API.get("/settings/aliexpress-tracking/sync-config");
      const data = res?.data || {};
      const enabled = data?.enabled === undefined ? true : Boolean(data.enabled);
      setTrackingSyncConfig({
        enabled,
        intervalHours: enabled ? (Number(data?.intervalHours) || 6) : 0,
      });
      setTrackingSyncStatus(data?.status || null);
    } catch (e) {
      setTrackingSyncError(readApiError(e, "Failed to load tracking cron settings"));
    } finally {
      setTrackingSyncLoading(false);
    }
  }, []);

  const saveTrackingSyncConfig = async () => {
    try {
      setTrackingSyncSaving(true);
      setTrackingSyncError("");
      const enabled = Boolean(trackingSyncConfig.enabled);
      const intervalHours = enabled ? Number(trackingSyncConfig.intervalHours || 6) : 0;
      const res = await API.put("/settings/aliexpress-tracking/sync-config", {
        enabled,
        intervalHours,
      });
      const data = res?.data || {};
      const nextEnabled = data?.enabled === undefined ? enabled : Boolean(data.enabled);
      setTrackingSyncConfig({
        enabled: nextEnabled,
        intervalHours: nextEnabled ? (Number(data?.intervalHours) || intervalHours || 6) : 0,
      });
      setTrackingSyncStatus(data?.status || trackingSyncStatus || null);
      toast.success("Tracking cron settings saved");
    } catch (e) {
      const msg = readApiError(e, "Failed to save tracking cron settings");
      setTrackingSyncError(msg);
      toast.error(msg);
    } finally {
      setTrackingSyncSaving(false);
    }
  };

  const fetchTrackingNow = async () => {
    try {
      setTrackingSyncNowLoading(true);
      setTrackingSyncError("");
      const res = await API.post("/aliexpress-tracking/fetch-now", { language: "en_US" });
      const data = res?.data || {};
      const status = {
        lastRunAt: new Date().toISOString(),
        lastResult: {
          count: Number(data?.count || 0),
          attempted: Number(data?.attempted || 0),
          failed: Number(data?.failed || 0),
          trackableItems: data?.trackableItems ?? null,
          trackableOrders: data?.trackableOrders ?? null,
        },
        lastError: null,
        source: "manual",
      };
      setTrackingSyncStatus(data?.status || status);
      const trackable = status.lastResult.trackableItems ?? status.lastResult.attempted;
      toast.success(`${status.lastResult.count} tracking update(s). Trackable: ${trackable}.`);
    } catch (e) {
      const msg = readApiError(e, "Failed to fetch tracking now");
      setTrackingSyncError(msg);
      toast.error(msg);
    } finally {
      setTrackingSyncNowLoading(false);
      await loadTrackingSyncConfig();
    }
  };

  const loadStockSyncConfig = useCallback(async () => {
    try {
      setStockSyncError("");
      setStockSyncLoading(true);
      const res = await API.get("/settings/aliexpress-stock/sync-config");
      const data = res?.data || {};
      setStockSyncConfig({
        enabled: data?.enabled === undefined ? true : Boolean(data.enabled),
        intervalHours: Number(data?.intervalHours) || 6,
        concurrentPerSecond: Number(data?.concurrentPerSecond) || 300,
        maxProductsPerRun: Number(data?.maxProductsPerRun) || DEFAULT_STOCK_MAX_PRODUCTS_PER_RUN,
        pausedByBulkImport: Boolean(data?.pausedByBulkImport),
      });
      setStockSyncStatus(data?.status || null);
      setStockSyncRunning(Boolean(data?.running));
      setStockIntervalOptions(
        Array.isArray(data?.allowedIntervalHours) && data.allowedIntervalHours.length
          ? data.allowedIntervalHours
          : DEFAULT_STOCK_INTERVAL_OPTIONS
      );
      setStockConcurrentOptions(
        Array.isArray(data?.allowedConcurrentPerSecond) && data.allowedConcurrentPerSecond.length
          ? data.allowedConcurrentPerSecond
          : DEFAULT_STOCK_CONCURRENT_OPTIONS
      );
    } catch (e) {
      setStockSyncError(readApiError(e, "Failed to load stock auto sync settings"));
    } finally {
      setStockSyncLoading(false);
    }
  }, []);

  const loadActiveImportJobs = useCallback(async () => {
    try {
      setActiveImportJobsLoading(true);
      const res = await API.get("/product/aliexpress/bulk/jobs?status=active&limit=10");
      setActiveImportJobs(Array.isArray(res?.data?.jobs) ? res.data.jobs : []);
    } catch {
      setActiveImportJobs([]);
    } finally {
      setActiveImportJobsLoading(false);
    }
  }, []);

  const cancelImportJob = async (jobId) => {
    const id = String(jobId || "").trim();
    if (!id || cancellingImportJobId) return;
    const confirmed = window.confirm(
      "Cancel this bulk import? Current item may finish, queued items will stop."
    );
    if (!confirmed) return;

    try {
      setCancellingImportJobId(id);
      const res = await API.post(`/product/aliexpress/bulk/jobs/${id}/cancel`);
      toast.info(res?.data?.message || "Bulk import cancellation requested.");
      await Promise.all([loadActiveImportJobs(), loadStockSyncConfig()]);
    } catch (e) {
      toast.error(readApiError(e, "Could not cancel bulk import job"));
    } finally {
      setCancellingImportJobId("");
    }
  };

  const saveStockSyncConfig = async () => {
    try {
      setStockSyncSaving(true);
      setStockSyncError("");
      const res = await API.put("/settings/aliexpress-stock/sync-config", {
        enabled: Boolean(stockSyncConfig.enabled),
        intervalHours: Number(stockSyncConfig.intervalHours),
        concurrentPerSecond: Number(stockSyncConfig.concurrentPerSecond),
      });
      const data = res?.data || {};
      setStockSyncConfig({
        enabled: data?.enabled === undefined ? true : Boolean(data.enabled),
        intervalHours: Number(data?.intervalHours) || Number(stockSyncConfig.intervalHours) || 6,
        concurrentPerSecond:
          Number(data?.concurrentPerSecond) || Number(stockSyncConfig.concurrentPerSecond) || 300,
        maxProductsPerRun:
          Number(data?.maxProductsPerRun) ||
          Number(stockSyncConfig.maxProductsPerRun) ||
          DEFAULT_STOCK_MAX_PRODUCTS_PER_RUN,
        pausedByBulkImport: Boolean(data?.pausedByBulkImport),
      });
      setStockSyncStatus(data?.status || stockSyncStatus || null);
      setStockSyncRunning(Boolean(data?.running));
      if (Array.isArray(data?.allowedIntervalHours) && data.allowedIntervalHours.length) {
        setStockIntervalOptions(data.allowedIntervalHours);
      }
      if (
        Array.isArray(data?.allowedConcurrentPerSecond) &&
        data.allowedConcurrentPerSecond.length
      ) {
        setStockConcurrentOptions(data.allowedConcurrentPerSecond);
      }
      toast.success("Stock sync settings saved");
    } catch (e) {
      const msg = readApiError(e, "Failed to save stock auto sync settings");
      setStockSyncError(msg);
      toast.error(msg);
    } finally {
      setStockSyncSaving(false);
    }
  };

  const syncStockNow = async () => {
    try {
      setStockSyncNowLoading(true);
      setStockSyncError("");
      const res = await API.post("/settings/aliexpress-stock/sync-now", {
        concurrentPerSecond: Number(stockSyncConfig.concurrentPerSecond),
      });
      const data = res?.data || {};
      setStockSyncStatus(data?.status || stockSyncStatus || null);
      setStockSyncRunning(Boolean(data?.running) || true);
      toast.success(data?.message || "Stock sync started");
    } catch (e) {
      const msg = readApiError(e, "Failed to sync stock now");
      setStockSyncError(msg);
      toast.error(msg);
    } finally {
      setStockSyncNowLoading(false);
      await loadStockSyncConfig();
    }
  };

  useEffect(() => {
    getToken();
    getAppSecret();
    loadTrackingSyncConfig();
    loadStockSyncConfig();
    loadActiveImportJobs();
  }, [getToken, getAppSecret, loadTrackingSyncConfig, loadStockSyncConfig, loadActiveImportJobs]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      loadTrackingSyncConfig();
      loadStockSyncConfig();
      loadActiveImportJobs();
    }, 5000);
    return () => clearInterval(intervalId);
  }, [loadTrackingSyncConfig, loadStockSyncConfig, loadActiveImportJobs]);

  useEffect(() => {
    if (!stockSyncRunning) return undefined;
    const intervalId = setInterval(() => {
      loadStockSyncConfig();
    }, 2500);
    return () => clearInterval(intervalId);
  }, [stockSyncRunning, loadStockSyncConfig]);

  useEffect(() => {
    const wasRunning = stockRunningPrevRef.current;
    if (wasRunning && !stockSyncRunning) {
      const updatedProducts = Number(stockSyncStatus?.lastResult?.updatedProducts || 0);
      const failedProducts = Number(stockSyncStatus?.lastResult?.failedProducts || 0);
      if (stockSyncStatus?.lastError) {
        toast.error(`Stock sync finished with error: ${String(stockSyncStatus.lastError)}`);
      } else {
        toast.success(
          `Stock sync completed. Updated ${updatedProducts} product(s), failed ${failedProducts}.`
        );
      }
    }
    stockRunningPrevRef.current = stockSyncRunning;
  }, [stockSyncRunning, stockSyncStatus]);

  useEffect(() => {
    const apiOrigin = getApiOrigin();
    const onMessage = async (event) => {
      if (apiOrigin && event.origin !== apiOrigin) return;
      const data = event?.data;
      if (!data || typeof data !== "object") return;
      if (data.type === "ALIEXPRESS_AUTH_SUCCESS") {
        authMessageReceivedRef.current = true;
        clearAuthorizeWatcher();
        setIsAuthorizing(false);
        toast.success("AliExpress authorized. Token updated.");
        await getToken();
        return;
      }
      if (data.type === "ALIEXPRESS_AUTH_FAILED") {
        authMessageReceivedRef.current = true;
        clearAuthorizeWatcher();
        setIsAuthorizing(false);
        const message = String(data?.message || "Authorization failed");
        toast.error(message);
        await getToken();
      }
    };

    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      clearAuthorizeWatcher();
    };
  }, [clearAuthorizeWatcher, getToken]);

  const computed = useMemo(() => {
    const t = tokenInfo || {};
    const expireAtProvider = normalizeEpoch(t.expire_time);
    const refreshValidProvider = normalizeEpoch(t.refresh_token_valid_time);

    const expiresInSec = Number(t.expires_in);
    const refreshExpiresInSec = Number(t.refresh_expires_in);

    const expiresAt = expireAtProvider ?? (Number.isFinite(expiresInSec) ? Date.now() + expiresInSec * 1000 : null);
    const refreshValidUntil = refreshValidProvider ?? (Number.isFinite(refreshExpiresInSec) ? Date.now() + refreshExpiresInSec * 1000 : null);

    const diff = expiresAt ? expiresAt - Date.now() : null;
    const statusTone =
      diff == null ? "neutral" : diff <= 0 ? "bad" : diff <= 24 * 60 * 60 * 1000 ? "warn" : "good";

    return {
      statusTone,
      expiresInHuman: fmtDurationFromSeconds(t.expires_in),
      expiresAtHuman: fmtDateTime(expiresAt),
      refreshValidUntilHuman: fmtDateTime(refreshValidUntil),
      expireTimeRaw: t.expire_time,
      expireTimeHuman: expireAtProvider ? fmtDateTime(expireAtProvider) : null,
    };
  }, [tokenInfo]);

  const isConnected = Boolean(tokenInfo?.access_token);

  const tokenRows = useMemo(() => {
    const t = tokenInfo || {};
    return [
      {
        k: "access_token",
        label: "Access Token (live)",
        value: t.access_token,
        sensitive: true,
      },
      {
        k: "refresh_token",
        label: "Refresh Token (live)",
        value: t.refresh_token,
        sensitive: true,
      },
      {
        k: "access_token_expires_in",
        label: "Access Token Expires In",
        value: t.expires_in == null ? "—" : `${computed.expiresInHuman} (${t.expires_in}s)`,
      },
      {
        k: "access_token_expires_at",
        label: "Access Token Expires At",
        value: computed.expiresAtHuman,
      },
      {
        k: "expire_time",
        label: "Access Token Expires At (provider)",
        value: computed.expireTimeHuman ? `${computed.expireTimeHuman} (${String(computed.expireTimeRaw)})` : (t.expire_time ?? "—"),
      },
      {
        k: "refresh_valid_until",
        label: "Refresh Token Valid Until",
        value: computed.refreshValidUntilHuman,
      },
      { k: "account", label: "Account", value: t.account ?? "—" },
      { k: "user_nick", label: "User Nick", value: t.user_nick ?? "—" },
      { k: "user_id", label: "User ID", value: t.user_id ?? "—" },
      { k: "seller_id", label: "Seller ID", value: t.seller_id ?? "—" },
      { k: "account_platform", label: "Account Platform", value: t.account_platform ?? "—" },
      { k: "locale", label: "Locale", value: t.locale ?? "—" },
      { k: "sp", label: "SP", value: t.sp ?? "—" },
      { k: "code", label: "Code", value: t.code ?? "—" },
      { k: "request_id", label: "Request ID", value: t.request_id ?? "—" },
      { k: "_trace_id_", label: "Trace ID", value: t._trace_id_ ?? "—" },
    ];
  }, [tokenInfo, computed]);

  const trackingSyncMeta = useMemo(() => {
    const lastRunAtMs = trackingSyncStatus?.lastRunAt ? Date.parse(trackingSyncStatus.lastRunAt) : null;
    const intervalMs = Number(trackingSyncConfig?.intervalHours || 0) * 60 * 60 * 1000;
    const nextRunAtMs =
      trackingSyncConfig?.enabled && lastRunAtMs && intervalMs > 0 ? lastRunAtMs + intervalMs : null;
    const lastResult = trackingSyncStatus?.lastResult || null;

    return {
      lastRunAtHuman: fmtDateTime(lastRunAtMs),
      nextRunAtHuman: trackingSyncConfig?.enabled ? fmtDateTime(nextRunAtMs) : "Disabled",
      source: trackingSyncStatus?.source || "--",
      lastResult,
      lastError: trackingSyncStatus?.lastError || "",
      updated: Number(lastResult?.count || 0),
      attempted: Number(lastResult?.attempted || 0),
      failed: Number(lastResult?.failed || 0),
      trackableItems: lastResult?.trackableItems ?? null,
      trackableOrders: lastResult?.trackableOrders ?? null,
    };
  }, [trackingSyncStatus, trackingSyncConfig]);

  const trackingSyncBadge = useMemo(() => {
    if (!trackingSyncConfig.enabled) {
      return { label: "Disabled", toneKey: "neutral" };
    }
    if (trackingSyncMeta.lastError) {
      return { label: "Needs Attention", toneKey: "bad" };
    }
    return { label: "Auto Fetch Enabled", toneKey: "good" };
  }, [trackingSyncConfig.enabled, trackingSyncMeta.lastError]);

  const stockSyncMeta = useMemo(() => {
    const lastRunAtMs = stockSyncStatus?.lastRunAt ? Date.parse(stockSyncStatus.lastRunAt) : null;
    const intervalMs = Number(stockSyncConfig?.intervalHours || 0) * 60 * 60 * 1000;
    const nextRunAtMs =
      stockSyncConfig?.enabled && lastRunAtMs && intervalMs > 0 ? lastRunAtMs + intervalMs : null;
    const maxProductsPerRun = Number.isFinite(Number(stockSyncConfig?.maxProductsPerRun))
      ? Number(stockSyncConfig.maxProductsPerRun)
      : Number(stockSyncStatus?.lastResult?.maxProductsPerRun) || DEFAULT_STOCK_MAX_PRODUCTS_PER_RUN;
    const pausedByBulkImport = Boolean(stockSyncConfig?.pausedByBulkImport);
    return {
      lastRunAtHuman: fmtDateTime(lastRunAtMs),
      nextRunAtHuman: pausedByBulkImport ? "Paused by import" : fmtDateTime(nextRunAtMs),
      source: stockSyncStatus?.source || "--",
      lastResult: stockSyncStatus?.lastResult || null,
      lastError: stockSyncStatus?.lastError || "",
      maxProductsPerRun,
      maxProductsPerRunLabel: fmtRunCap(maxProductsPerRun),
      pausedByBulkImport,
    };
  }, [stockSyncStatus, stockSyncConfig]);

  const stockSyncLive = useMemo(() => {
    const progress = stockSyncStatus?.progress || null;
    if (!progress) return null;
    const totalRows = Number(progress.totalRows || 0);
    const processedRows = Number(progress.processedRows || 0);
    const inFlightRows = Number(progress.inFlightRows || 0);
    const remainingRows = Number(progress.remainingRows || 0);
    const progressPercent =
      totalRows > 0
        ? Math.min(100, Math.max(0, Number(progress.progressPercent || 0)))
        : 0;
    const countries = Array.isArray(progress.countries) ? progress.countries : [];
    return {
      totalRows,
      processedRows,
      inFlightRows,
      remainingRows,
      progressPercent,
      concurrentPerSecond: Number(progress.concurrentPerSecond || 0),
      maxProductsPerRun: Number(progress.maxProductsPerRun || stockSyncConfig.maxProductsPerRun || DEFAULT_STOCK_MAX_PRODUCTS_PER_RUN),
      maxProductsPerRunLabel: fmtRunCap(progress.maxProductsPerRun || stockSyncConfig.maxProductsPerRun || DEFAULT_STOCK_MAX_PRODUCTS_PER_RUN),
      eligibleRows: Number(progress.eligibleRows || 0),
      attemptedProducts: Number(progress.attemptedProducts || 0),
      updatedProducts: Number(progress.updatedProducts || 0),
      failedProducts: Number(progress.failedProducts || 0),
      unchangedProducts: Number(progress.unchangedProducts || 0),
      retriedProducts: Number(progress.retriedProducts || 0),
      retryAttempts: Number(progress.retryAttempts || 0),
      countries,
    };
  }, [stockSyncStatus, stockSyncConfig.maxProductsPerRun]);

  const stockSyncBadge = useMemo(() => {
    if (stockSyncRunning) {
      return { label: "Sync Running", toneKey: "good" };
    }
    if (stockSyncMeta.pausedByBulkImport) {
      return { label: "Paused by import", toneKey: "warn" };
    }
    return { label: "Idle", toneKey: "neutral" };
  }, [stockSyncRunning, stockSyncMeta.pausedByBulkImport]);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6 md:mb-8">
          <div className="min-w-0">
            <h1 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">
              AliExpress Integration
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Token lifecycle controls and live status view.
            </p>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
                <Clock size={14} className="text-slate-500" />
                {lastUpdatedLabel ? (
                  <span>
                    Updated{" "}
                    <span className="font-semibold text-slate-900">{lastUpdatedLabel}</span>
                  </span>
                ) : (
                  <span>Not updated yet</span>
                )}
              </span>

              {error ? (
                <span className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs text-rose-700">
                  <AlertTriangle size={14} />
                  {error}
                </span>
              ) : (
                <span
                  className={cx(
                    "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold",
                    tone[computed.statusTone].badge
                  )}
                >
                  <span className={cx("h-2 w-2 rounded-full", tone[computed.statusTone].dot)} />
                  {isConnected ? "Connected" : "Not Connected"}
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={getToken}
              disabled={loading}
              className={cx(
                "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border transition-colors",
                loading
                  ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                  : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50"
              )}
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Reload
            </button>

            <button
              onClick={refreshToken}
              disabled={refreshing}
              className={cx(
                "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white transition-colors",
                refreshing ? "bg-slate-300 cursor-not-allowed" : "bg-slate-900 hover:bg-[#D2042D]"
              )}
            >
              <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
              Refresh Token
            </button>

            <button
              onClick={openAuthorize}
              disabled={isAuthorizing}
              className={cx(
                "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border transition-colors",
                isAuthorizing
                  ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                  : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50"
              )}
              title="Use when both Access Token and Refresh Token are expired"
            >
              <ExternalLink size={16} />
              {isAuthorizing ? "Authorizing..." : "Authorize Token"}
            </button>

            <button
              className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors"
              onClick={() => toast.info("Settings are below (App Secret, Tracking Cron, and Stock Sync).")}
            >
              <Settings size={16} />
              Settings
            </button>
          </div>
        </div>

        {/* Private settings */}
        <Card className="p-4 md:p-5 mb-6 md:mb-8">
          <SectionHead
            title="Private Credentials"
            subtitle="Stored privately. Values are masked on screen by default."
          />

          <div className="mt-4 grid grid-cols-1 gap-3 md:gap-4">
            <SecretField
              label="App Secret"
              hint="From AliExpress developer console"
              valueRaw={appSecret}
              onChangeRaw={setAppSecret}
              onSave={saveAppSecret}
              saving={savingSecret}
            />
          </div>
        </Card>

        {/* Live token status */}
        <Card className="p-4 md:p-5">
          <SectionHead
            title="Current Token Status"
            subtitle="Live payload from /aliexpress/gettoken. Access/refresh tokens are masked."
            right={
              <span className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">
                Source: /aliexpress/gettoken
              </span>
            }
          />

          <div className="mt-4">
            {loading && !tokenInfo ? (
              <div className="grid grid-cols-1 gap-2">
                <div className="animate-pulse rounded-xl bg-slate-100 h-10" />
                <div className="animate-pulse rounded-xl bg-slate-100 h-10" />
                <div className="animate-pulse rounded-xl bg-slate-100 h-10" />
              </div>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-slate-200">
                <table className="min-w-full text-sm">
                  <tbody className="divide-y divide-slate-100">
                    {tokenRows.map((r) => {
                      const isSensitive = r.sensitive;
                      const raw = r.value;
                      const shown = isSensitive ? maskMiddle50(raw) : (raw == null || raw === "" ? "—" : String(raw));

                      return (
                        <tr key={r.k} className="bg-white">
                          <td className="py-3 px-4 text-slate-500 whitespace-nowrap">
                            {r.label}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="font-mono text-xs bg-slate-50 border border-slate-200 px-2 py-1 rounded-xl break-all">
                                {shown}
                              </span>

                              {raw ? (
                                <button
                                  onClick={async () => {
                                    const ok = await copyToClipboard(raw);
                                    ok ? toast.success("Copied") : toast.error("Copy failed");
                                  }}
                                  className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                  title="Copy raw value"
                                >
                                  <Copy size={14} />
                                  Copy
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 text-slate-400" size={18} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900">How to use</p>
                  <p className="mt-1 text-xs text-slate-600">
                    Normal rotation: <span className="font-semibold">Refresh Token</span>.
                    If refresh fails (both expired), use{" "}
                    <span className="font-semibold">Authorize Token</span> to re-login in a browser.
                  </p>
                </div>
              </div>
            </div>

            {!loading && !tokenInfo ? (
              <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs text-rose-700">
                <AlertTriangle size={14} />
                No token payload loaded.
              </div>
            ) : null}
          </div>
        </Card>

        {/* Tracking auto fetch settings */}
        <Card className="p-4 md:p-5 mt-6 md:mt-8">
          <SectionHead
            title="AliExpress Tracking Cron"
            subtitle="Auto-fetch logistics events for approved AliExpress orders and expose safe shipment updates to customers."
            right={
              <span
                className={cx(
                  "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold",
                  tone[trackingSyncBadge.toneKey].badge
                )}
              >
                <span
                  className={cx(
                    "h-2 w-2 rounded-full",
                    tone[trackingSyncBadge.toneKey].dot
                  )}
                />
                {trackingSyncLoading ? (
                  <RefreshCw size={14} className="animate-spin" />
                ) : (
                  <Clock size={14} />
                )}
                {trackingSyncBadge.label}
              </span>
            }
          />

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <label className="rounded-2xl border border-slate-200 bg-white px-3 py-3 flex items-center justify-between gap-3">
              <span className="text-sm font-semibold text-slate-900">Auto Fetch Enabled</span>
              <input
                type="checkbox"
                checked={Boolean(trackingSyncConfig.enabled)}
                onChange={(e) =>
                  setTrackingSyncConfig((prev) => ({
                    ...prev,
                    enabled: Boolean(e.target.checked),
                    intervalHours: e.target.checked ? (Number(prev.intervalHours) || 6) : 0,
                  }))
                }
                className="h-4 w-4 accent-slate-900"
              />
            </label>

            <label className="rounded-2xl border border-slate-200 bg-white px-3 py-3">
              <p className="text-xs font-semibold text-slate-500">Interval</p>
              <select
                value={String(trackingSyncConfig.enabled ? trackingSyncConfig.intervalHours : 0)}
                onChange={(e) => {
                  const value = Number(e.target.value) || 0;
                  setTrackingSyncConfig((prev) => ({
                    ...prev,
                    enabled: value > 0,
                    intervalHours: value,
                  }));
                }}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
              >
                {TRACKING_INTERVAL_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3">
              <p className="text-xs font-semibold text-slate-500">Concurrency</p>
              <p className="mt-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-900">
                250 orders/run
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3">
              <p className="text-xs font-semibold text-slate-500">Last Source</p>
              <p className="mt-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-900">
                {trackingSyncMeta.source}
              </p>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={loadTrackingSyncConfig}
              disabled={trackingSyncLoading}
              className={cx(
                "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border transition-colors",
                trackingSyncLoading
                  ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                  : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50"
              )}
            >
              <RefreshCw size={16} className={trackingSyncLoading ? "animate-spin" : ""} />
              Reload Status
            </button>

            <button
              type="button"
              onClick={saveTrackingSyncConfig}
              disabled={trackingSyncSaving}
              className={cx(
                "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white transition-colors",
                trackingSyncSaving ? "bg-slate-300 cursor-not-allowed" : "bg-slate-900 hover:bg-[#D2042D]"
              )}
            >
              <Save size={16} className={trackingSyncSaving ? "opacity-70" : ""} />
              Save Cron Settings
            </button>

            <button
              type="button"
              onClick={fetchTrackingNow}
              disabled={trackingSyncNowLoading}
              className={cx(
                "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border transition-colors",
                trackingSyncNowLoading
                  ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                  : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50"
              )}
            >
              <RefreshCw size={16} className={trackingSyncNowLoading ? "animate-spin" : ""} />
              Fetch Tracking Now
            </button>
          </div>

          {trackingSyncError ? (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs text-rose-700">
              <AlertTriangle size={14} />
              {trackingSyncError}
            </div>
          ) : null}

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">Last Run</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{trackingSyncMeta.lastRunAtHuman}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">Next Run</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{trackingSyncMeta.nextRunAtHuman}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">Trackable Items</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {trackingSyncMeta.trackableItems === null ? "--" : trackingSyncMeta.trackableItems}
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">Trackable Orders</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {trackingSyncMeta.trackableOrders === null ? "--" : trackingSyncMeta.trackableOrders}
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">Updated</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{trackingSyncMeta.updated}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">Failed</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{trackingSyncMeta.failed}</p>
            </div>
          </div>

          {trackingSyncMeta.lastError ? (
            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-3 text-xs text-amber-800">
              Last tracking error: {String(trackingSyncMeta.lastError)}
            </div>
          ) : null}
        </Card>

        {/* Stock auto sync settings */}
        <Card className="p-4 md:p-5 mt-6 md:mt-8">
          <SectionHead
            title="AliExpress Stock Sync (Cron)"
            subtitle="Country-wise stock sync with auto cron, manual start, retries, and live progress. Sync updates sku_available_stock only."
            right={
              <span
                className={cx(
                  "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold",
                  tone[stockSyncBadge.toneKey].badge
                )}
              >
                <span
                  className={cx(
                    "h-2 w-2 rounded-full",
                    tone[stockSyncBadge.toneKey].dot
                  )}
                />
                {stockSyncBadge.toneKey === "warn" ? (
                  <PauseCircle size={14} />
                ) : stockSyncRunning ? (
                  <RefreshCw size={14} className="animate-spin" />
                ) : (
                  <Clock size={14} />
                )}
                {stockSyncBadge.label}
              </span>
            }
          />

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <label className="rounded-2xl border border-slate-200 bg-white px-3 py-3 flex items-center justify-between gap-3">
              <span className="text-sm font-semibold text-slate-900">Auto Sync Enabled</span>
              <input
                type="checkbox"
                checked={Boolean(stockSyncConfig.enabled)}
                onChange={(e) =>
                  setStockSyncConfig((prev) => ({ ...prev, enabled: Boolean(e.target.checked) }))
                }
                className="h-4 w-4 accent-slate-900"
              />
            </label>

            <label className="rounded-2xl border border-slate-200 bg-white px-3 py-3">
              <p className="text-xs font-semibold text-slate-500">Interval (hours)</p>
              <select
                value={String(stockSyncConfig.intervalHours)}
                onChange={(e) =>
                  setStockSyncConfig((prev) => ({
                    ...prev,
                    intervalHours: Number(e.target.value) || 6,
                  }))
                }
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
              >
                {stockIntervalOptions.map((h) => (
                  <option key={h} value={h}>
                    Every {h} hour{Number(h) > 1 ? "s" : ""}
                  </option>
                ))}
              </select>
            </label>

            <label className="rounded-2xl border border-slate-200 bg-white px-3 py-3">
              <p className="text-xs font-semibold text-slate-500">Max API calls/sec</p>
              <select
                value={String(stockSyncConfig.concurrentPerSecond)}
                onChange={(e) =>
                  setStockSyncConfig((prev) => ({
                    ...prev,
                    concurrentPerSecond: Number(e.target.value) || 300,
                  }))
                }
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
              >
                {stockConcurrentOptions.map((v) => (
                  <option key={v} value={v}>
                    {v} req/sec
                  </option>
                ))}
              </select>
            </label>

            <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3">
              <p className="text-xs font-semibold text-slate-500">Products per run</p>
              <p className="mt-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-900">
                {stockSyncMeta.maxProductsPerRunLabel}
              </p>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={loadStockSyncConfig}
              disabled={stockSyncLoading}
              className={cx(
                "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border transition-colors",
                stockSyncLoading
                  ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                  : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50"
              )}
            >
              <RefreshCw size={16} className={stockSyncLoading ? "animate-spin" : ""} />
              Reload Status
            </button>

            <button
              type="button"
              onClick={saveStockSyncConfig}
              disabled={stockSyncSaving}
              className={cx(
                "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white transition-colors",
                stockSyncSaving ? "bg-slate-300 cursor-not-allowed" : "bg-slate-900 hover:bg-[#D2042D]"
              )}
            >
              <Save size={16} className={stockSyncSaving ? "opacity-70" : ""} />
              Save Cron Settings
            </button>

            <button
              type="button"
              onClick={syncStockNow}
              disabled={stockSyncNowLoading || stockSyncRunning || stockSyncMeta.pausedByBulkImport}
              className={cx(
                "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border transition-colors",
                stockSyncNowLoading || stockSyncRunning || stockSyncMeta.pausedByBulkImport
                  ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                  : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50"
              )}
            >
              <RefreshCw size={16} className={stockSyncNowLoading ? "animate-spin" : ""} />
              Sync Next Batch Now
            </button>
          </div>

          <p className="mt-3 text-xs text-slate-500">
            Each stock sync run processes {stockSyncMeta.maxProductsPerRunLabel} product-country rows at 300 req/sec.
          </p>

          {stockSyncMeta.pausedByBulkImport ? (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs text-amber-800">
              <PauseCircle size={14} />
              Stock cron is paused while bulk product import is running.
            </div>
          ) : null}

          {(activeImportJobsLoading || activeImportJobs.length > 0) ? (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900">Active Bulk Imports</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Cron stays paused while these imports are queued or running.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={loadActiveImportJobs}
                    disabled={activeImportJobsLoading}
                    className={cx(
                      "inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-xs font-semibold border transition-colors",
                      activeImportJobsLoading
                        ? "border-slate-200 bg-slate-100 text-slate-400 cursor-wait"
                        : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50"
                    )}
                  >
                    <RefreshCw size={14} className={activeImportJobsLoading ? "animate-spin" : ""} />
                    Refresh
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      window.location.href = "/administrator/products/addProduct/aliexpress";
                    }}
                    className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-900 hover:bg-slate-50"
                  >
                    <PackageSearch size={14} />
                    Open Importer
                  </button>
                </div>
              </div>

              {!activeImportJobs.length ? (
                <div className="mt-3 rounded-xl border border-slate-200 bg-white px-3 py-4 text-xs text-slate-500">
                  {activeImportJobsLoading ? "Loading active import jobs..." : "No active import jobs."}
                </div>
              ) : (
                <div className="mt-3 divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
                  {activeImportJobs.map((job) => {
                    const jobId = String(job?._id || "");
                    const total = Number(job?.summary?.total || 0);
                    const processed = Number(job?.summary?.processed || 0);
                    const imported = Number(job?.summary?.imported || 0);
                    const failed = Number(job?.summary?.failed || 0);
                    const exists = Number(job?.summary?.exists || 0);
                    const progressPercent = total > 0
                      ? Math.min(100, Math.round((processed / total) * 100))
                      : Number(job?.progressPercent || 0);
                    const rateLimit = job?.rateLimit || {};
                    const isPaused = Boolean(rateLimit?.paused);
                    const cancelling = cancellingImportJobId === jobId;

                    return (
                      <div key={jobId} className="px-3 py-3">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="truncate text-xs font-semibold text-slate-900">Job #{jobId}</p>
                              <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                                {String(job?.status || "queued")}
                              </span>
                              {job?.cancelRequested ? (
                                <span className="rounded-full border border-rose-100 bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700">
                                  cancel requested
                                </span>
                              ) : null}
                              {isPaused ? (
                                <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                                  API paused: {String(rateLimit.pauseReason || "ban detection")}
                                </span>
                              ) : null}
                            </div>

                            <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200">
                              <div
                                className="h-full rounded-full bg-[#D2042D]"
                                style={{ width: `${progressPercent}%` }}
                              />
                            </div>

                            <p className="mt-2 text-xs text-slate-600">
                              {processed}/{total} processed - imported {imported} - skipped {exists} - failed {failed} - {job?.workers || 300} API workers
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={() => cancelImportJob(jobId)}
                            disabled={Boolean(job?.cancelRequested) || Boolean(cancellingImportJobId)}
                            className={cx(
                              "inline-flex items-center justify-center gap-2 rounded-2xl border px-3 py-2 text-xs font-semibold transition-colors",
                              Boolean(job?.cancelRequested) || Boolean(cancellingImportJobId)
                                ? "border-rose-100 bg-rose-50 text-rose-400 cursor-not-allowed"
                                : "border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
                            )}
                          >
                            {cancelling ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <StopCircle size={14} />
                            )}
                            {cancelling ? "Cancelling..." : "Cancel"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : null}

          {stockSyncError ? (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs text-rose-700">
              <AlertTriangle size={14} />
              {stockSyncError}
            </div>
          ) : null}

          {stockSyncLive ? (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-slate-900">
                  Live Progress {stockSyncRunning ? "(Running)" : "(Latest Snapshot)"}
                </p>
                <span className="text-xs text-slate-500">
                  {stockSyncLive.concurrentPerSecond || "--"} req/sec - max {stockSyncLive.maxProductsPerRunLabel}/run
                </span>
              </div>

              <div className="mt-3">
                <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-[#D2042D] transition-all"
                    style={{ width: `${stockSyncLive.progressPercent}%` }}
                  />
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-600">
                  <span>Progress: {stockSyncLive.progressPercent}%</span>
                  <span>Total: {stockSyncLive.totalRows}</span>
                  <span>Eligible: {stockSyncLive.eligibleRows}</span>
                  <span>Run cap: {stockSyncLive.maxProductsPerRunLabel}</span>
                  <span>Done: {stockSyncLive.processedRows}</span>
                  <span>Working: {stockSyncLive.inFlightRows}</span>
                  <span>Left: {stockSyncLive.remainingRows}</span>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 text-xs">
                <div className="rounded-xl border border-slate-200 bg-white px-2 py-2 text-slate-700">
                  Attempted: <span className="font-semibold">{stockSyncLive.attemptedProducts}</span>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white px-2 py-2 text-slate-700">
                  Updated: <span className="font-semibold">{stockSyncLive.updatedProducts}</span>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white px-2 py-2 text-slate-700">
                  Failed: <span className="font-semibold">{stockSyncLive.failedProducts}</span>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white px-2 py-2 text-slate-700">
                  Unchanged: <span className="font-semibold">{stockSyncLive.unchangedProducts}</span>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white px-2 py-2 text-slate-700">
                  Retried rows: <span className="font-semibold">{stockSyncLive.retriedProducts}</span>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white px-2 py-2 text-slate-700">
                  Retry attempts: <span className="font-semibold">{stockSyncLive.retryAttempts}</span>
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-slate-200 bg-white">
                <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
                  <p className="text-xs font-semibold text-slate-700">Country Progress</p>
                  <p className="text-[11px] text-slate-500">Top countries by row count</p>
                </div>
                <div className="max-h-56 overflow-auto divide-y divide-slate-100">
                  {(stockSyncLive.countries || []).slice(0, 12).map((c) => {
                    const total = Number(c?.totalRows || 0);
                    const processed = Number(c?.processedRows || 0);
                    const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
                    return (
                      <div key={String(c?.countryCode || 'UNKNOWN')} className="px-3 py-2">
                        <div className="flex items-center justify-between gap-2 text-xs text-slate-700">
                          <span className="font-semibold">{String(c?.countryCode || '--')}</span>
                          <span>
                            {processed}/{total} · updated {Number(c?.updatedProducts || 0)} · failed {Number(c?.failedProducts || 0)}
                          </span>
                        </div>
                        <div className="mt-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-slate-900"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                  {!stockSyncLive.countries?.length ? (
                    <div className="px-3 py-4 text-xs text-slate-500">No country progress yet.</div>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                Last Run
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{stockSyncMeta.lastRunAtHuman}</p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                Next Run
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {stockSyncConfig.enabled ? stockSyncMeta.nextRunAtHuman : "Disabled"}
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                Last Source
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{stockSyncMeta.source}</p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                Attempted
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {stockSyncMeta.lastResult?.attemptedProducts ?? "--"}
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                Updated
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {stockSyncMeta.lastResult?.updatedProducts ?? "--"}
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                Failed
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {stockSyncMeta.lastResult?.failedProducts ?? "--"}
              </p>
            </div>
          </div>

          {stockSyncMeta.lastError ? (
            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-3 text-xs text-amber-800">
              Last sync error: {String(stockSyncMeta.lastError)}
            </div>
          ) : null}
        </Card>
      </div>
    </div>
  );
}
